import { Key, useMemo } from "react";
import { Button, Card, CardBody, Listbox, ListboxItem } from "@heroui/react";
import {
  ChatCircleTextIcon,
  CloudArrowUpIcon,
  LockIcon,
  PaintRollerIcon,
  QuestionIcon,
  SignOutIcon,
  TrashSimpleIcon,
  UserCircleIcon,
  UserCirclePlusIcon,
} from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";
import { useOpenState } from "@/shared/hooks/useOpenState";
import { ManageDeleteAccount } from "./components/ManageDeleteAccount";
import { ManageTheme } from "./components/ManageTheme";
import { ManageEncryption } from "@/features/encryption/components/ManageEncryption";
import { ManageGuestSignIn } from "./components/ManageGuestSignIn";
import { useBoundStore } from "@/app/store";
import { useNavigate } from "react-router";
import { supabase } from "@/services/supabase/client";
import { ICON_SIZE } from "@/config/constants";
import { usePageLayout } from "@/app/hooks/usePageLayout";

export function SettingsRoute() {
  const clearUser = useBoundStore((state) => state.user.clear);
  const isAnonymous = useBoundStore(useShallow((state) => state.user.isAnonymous));
  const navigate = useNavigate();

  const {
    id: manageGuestSignInId,
    isOpen: isGuestSignInOpen,
    handleOpenChange: handleGuestSignInOpenChange,
  } = useOpenState({
    id: "manage-guest-sign-in",
  });
  const {
    id: manageThemeId,
    isOpen: isThemeModalOpen,
    handleOpenChange: handleThemeModalOpenChange,
  } = useOpenState({
    id: "manage-theme",
  });
  const {
    id: manageDeleteAccountId,
    isOpen: isDeleteModalOpen,
    handleOpenChange: handleDeleteModalOpenChange,
  } = useOpenState({
    id: "manage-delete-account",
  });
  const {
    id: manageEncryptionId,
    isOpen: isEncryptionModalOpen,
    handleOpenChange: handleEncryptionModalOpenChange,
  } = useOpenState({
    id: "manage-encryption",
  });

  const handleAction = (key: Key) => {
    if (key === "sign-in") {
      handleGuestSignInOpenChange(true);
    } else if (key === "profile") {
      navigate("/settings/profile");
    } else if (key === "theme") {
      handleThemeModalOpenChange(true);
    } else if (key === "encryption") {
      handleEncryptionModalOpenChange(true);
    } else if (key === "delete") {
      handleDeleteModalOpenChange(true);
    } else if (key === "help") {
      navigate("/settings/help");
    } else if (key === "feedback") {
      navigate("/settings/feedback");
    } else if (key === "sign-out") {
      clearUser();
      supabase.auth.signOut();
      navigate("/", { replace: true });
    }
  };

  // Static layout config - usePageLayout is optimal here
  const pageLayoutConfig = useMemo(
    () => ({
      showBottomNavigation: true,
      navbar: { hideBack: true, title: "Settings" },
    }),
    []
  );

  usePageLayout(pageLayoutConfig);

  return (
    <div className="space-y-4">
      {isAnonymous && (
        <Card className="mx-auto max-w-lg">
          <CardBody className="gap-3 p-4">
            <div className="flex items-center gap-3">
              <CloudArrowUpIcon size={32} className="text-primary shrink-0" />
              <div>
                <p className="font-semibold text-sm">Guest Mode</p>
                <p className="text-default-500 text-xs">
                  Create an account to back up and sync your notes and tasks across all your devices.
                </p>
              </div>
            </div>
            <Button
              color="primary"
              size="sm"
              variant="flat"
              fullWidth
              startContent={<UserCirclePlusIcon size={ICON_SIZE.sm} />}
              onPress={() => handleGuestSignInOpenChange(true)}
            >
              Sign In / Link Account
            </Button>
          </CardBody>
        </Card>
      )}

      <Card className="mx-auto max-w-lg">
        <CardBody>
          <Listbox aria-label="Settings" onAction={handleAction}>
            
            <ListboxItem
              key="profile"
              startContent={<UserCircleIcon size={ICON_SIZE.lg} />}
              description={isAnonymous ? "Guest Profile" : "Manage your profile settings"}
            >
              Profile
            </ListboxItem>
            <ListboxItem
              key="theme"
              startContent={<PaintRollerIcon size={ICON_SIZE.lg} />}
              description="Choose your preferred theme"
            >
              Theme
            </ListboxItem>
            <ListboxItem
              key="encryption"
              startContent={<LockIcon size={ICON_SIZE.lg} />}
              description="Setup end-to-end encryption"
            >
              Encryption
            </ListboxItem>
            <ListboxItem
              key="delete"
              startContent={<TrashSimpleIcon size={ICON_SIZE.lg} />}
              description="Permanently delete your account"
            >
              Delete account
            </ListboxItem>
            <ListboxItem
              key="help"
              startContent={<QuestionIcon size={ICON_SIZE.lg} />}
              description="Get help and information"
            >
              Help
            </ListboxItem>
            <ListboxItem
              key="feedback"
              startContent={<ChatCircleTextIcon size={ICON_SIZE.lg} />}
              description="Share your feedback with us"
            >
              Feedback
            </ListboxItem>
            <ListboxItem
              key="sign-out"
              startContent={<SignOutIcon size={ICON_SIZE.lg} />}
              description={isAnonymous ? "Clear guest session and return to sign in" : "Sign out of your account"}
            >
              Sign out
            </ListboxItem>
          </Listbox>
        </CardBody>
      </Card>

      <ManageGuestSignIn
        id={manageGuestSignInId}
        externalState={{ isOpen: isGuestSignInOpen, onOpenChange: handleGuestSignInOpenChange }}
      />
      <ManageTheme
        id={manageThemeId}
        externalState={{ isOpen: isThemeModalOpen, onOpenChange: handleThemeModalOpenChange }}
      />
      <ManageEncryption
        id={manageEncryptionId}
        externalState={{ isOpen: isEncryptionModalOpen, onOpenChange: handleEncryptionModalOpenChange }}
      />
      <ManageDeleteAccount
        id={manageDeleteAccountId}
        externalState={{ isOpen: isDeleteModalOpen, onOpenChange: handleDeleteModalOpenChange }}
      />
    </div>
  );
}
